/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import PersistentMapHost from '@ion/applications/src/components/persistent/PersistentMapHost';
import RootManager from '@ion/dls/src/client/RootManager';
import {isSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {removeAllUIComponents, removeLayerIfExist} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {MapWidgetIdentifiers} from '@ion/dls/src/maps/types/types';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {TrackingMode} from '@ion/dls/src/types/types';
import {Route} from '@/routes/RouteDefinitions';
import {sideSheetViewSelector, trackingModeSelector} from '@/store/selectors/tracking-selectors';
import TrackingMapContainer from './containers/TrackingMapContainer';

export const LIVE_MAP_CONTAINER_ID = 'live-map';

const PersistentTrackingMapHost = () => {
  const trackingMode        = useSelector(trackingModeSelector);
  const sideSheetView       = useSelector(sideSheetViewSelector);
  const {isCurrent}         = useThemeContext();
  const isPhone             = useIsPhone();
  const [onShow, setOnShow] = useState(false);
  const mapViewRef          = useRef<__esri.MapView>(null);
  const mapIdsRef           = useRef<string[]>([]);

  const unMount = useCallback((id) => {
    RootManager.removeContainer(RootManager.getContainer(id));
  }, []);

  useEffect(() => {
    if (!onShow && mapViewRef.current) {
      removeAllUIComponents(mapViewRef.current);
      Object.values(MapWidgetIdentifiers).forEach(unMount);
      mapIdsRef.current.forEach((id) => {
        removeLayerIfExist(mapViewRef.current.map, id);
      });
      mapIdsRef.current = [];
    };
  }, [onShow]);

  const onSetView = useCallback((view: __esri.MapView) => {
    mapViewRef.current = view;
  }, []);

  const onSetMapId = useCallback((mapId: string) => {
    mapIdsRef.current = [
      ...mapIdsRef.current,
      mapId
    ];
  }, []);

  // the map is one element across this host's renders, so re-rendering the
  // host never re-renders the map (every layer and widget) with it ...
  const trackingMap = useMemo(() => (
    <TrackingMapContainer renderMapWidgets={onShow}
      onSetView={onSetView}
      onSetMapId={onSetMapId} />
  ), [onShow, onSetView, onSetMapId]);

  // current theme on a phone: the asset grid takes the map's place, so the map
  // (drawn over its slot, above the grid) is hidden - kept mounted, with its
  // widgets and layers, for switching back ...
  const isMapHidden = isCurrent && isPhone && isSheetTableView(sideSheetView);

  // current theme: the map resizes once its slot has settled - after the
  // side sheet has finished opening or closing, never during it ...
  return (
    <PersistentMapHost containerId={LIVE_MAP_CONTAINER_ID}
      condition={trackingMode === TrackingMode.Live}
      routeToMatch={`/${Route.TRACKING2}`}
      settleResize={isCurrent}
      hidden={isMapHidden}
      onShow={setOnShow}>
      {trackingMap}
    </PersistentMapHost>
  );
};

export default memo(PersistentTrackingMapHost);
